import { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Ban } from 'lucide-react';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useSubscriptionProcess } from '../../contexts/SubscriptionProcessContext';
import { abandonDevis } from '../api/abandonDevis';

interface AbandonQuoteButtonProps {
  /** Id du devis à abandonner. */
  devisId?: string;
  /**
   * Visible uniquement quand le devis est encore "en cours" (ni abandonné, ni déjà
   * transformé en contrat). Calculé par le flux appelant (emprunteur ou générique).
   */
  canAbandon: boolean;
}

/**
 * Action discrète d'abandon d'un devis, partagée par les flux de contractualisation
 * (emprunteur et générique CBNO / chômage / GLI). Affiche une confirmation puis appelle
 * PUT /api/backoffice/devis/{devisId}/abandon et rafraîchit le process.
 */
export const AbandonQuoteButton: FC<AbandonQuoteButtonProps> = ({
  devisId,
  canAbandon
}) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization]);
  const { refreshSubscriptionProcess } = useSubscriptionProcess();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!devisId) return;
    setIsLoading(true);
    try {
      const res = await abandonDevis(devisId);
      if (res.isSuccess) {
        // Toaster vert affiché automatiquement via OperationResponse.notification.
        refreshSubscriptionProcess();
      } else {
        // Échec métier (HTTP 200, isSuccess=false) : ex. "Devis introuvable.".
        toast.error(res.errorMessage || t('CONTRACTUALIZATION.ABANDON.ERROR'));
      }
    } catch (error) {
      console.error('Error abandoning quote:', error);
      toast.error(t('CONTRACTUALIZATION.ABANDON.ERROR'));
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  }, [devisId, refreshSubscriptionProcess, t]);

  // Visible uniquement pour un devis en cours (ni abandonné, ni déjà transformé en contrat).
  if (!canAbandon || !devisId) return null;

  return (
    <div className="mt-6 flex justify-end">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        disabled={isLoading}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-500 shadow-sm transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Ban className="size-4" />
        {t('CONTRACTUALIZATION.ABANDON.BUTTON')}
      </button>
      <ConfirmationOverlay
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={handleConfirm}
        title={t('CONTRACTUALIZATION.ABANDON.TITLE')}
        description={t('CONTRACTUALIZATION.ABANDON.DESCRIPTION')}
        confirmLabel={t('CONTRACTUALIZATION.ABANDON.CONFIRM')}
        variant="danger"
        isLoading={isLoading}
      />
    </div>
  );
};
